import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchWrapper } from "../utils/fetchWrapper";
import { toast } from "react-hot-toast";
import {
  MdBusiness,
  MdSettings,
  MdHealthAndSafety,
  MdCheckCircle,
  MdError,
  MdCloudUpload,
} from "react-icons/md";
import "./InstallationPage.css";

const InstallationPage = () => {
  const navigate = useNavigate();
  const { user, isInstalled } = useSelector((state) => state.auth);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState({
    database: "checking",
    kafka: "checking",
    redis: "checking",
  });

  const [formData, setFormData] = useState({
    appName: "Microsite Cloud",
    appLogo: "",
    primaryDomain: window.location.hostname,
    adminEmail: user?.email || "",
  });

  useEffect(() => {
    // If already installed, redirect to dashboard
    if (isInstalled) {
      navigate("/");
    }
    // Perform health checks on step 3
    if (step === 3) {
      checkHealth();
    }
  }, [isInstalled, navigate, step]);

  const checkHealth = async () => {
    setHealth({ database: "checking", kafka: "checking", redis: "checking" });
    // Simulate health checks
    setTimeout(() => {
      setHealth({
        database: "online",
        kafka: "online",
        redis: "online",
      });
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNext = () => setStep(step + 1);
  const handlePrev = () => setStep(step - 1);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetchWrapper.post("/app/setup", formData);
      toast.success("Installation completed successfully!");
      // Update local storage and state (handled by a full page reload or manual update)
      localStorage.setItem("isInstalled", "true");
      window.location.href = "/"; // Force reload to refresh all states
    } catch (err) {
      toast.error(err.response?.data?.message || "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="installation-wrapper">
      <div className="installation-card shadow-lg">
        <div className="installation-sidebar">
          <div className="sidebar-header">
            <img
              src="/logo.png"
              alt="Microsite Cloud"
              className="install-logo"
            />
            <h5>Microsite Cloud</h5>
            <p>Setup Wizard</p>
          </div>
          <div className="step-indicators">
            <div
              className={`step-item ${step === 1 ? "active" : step > 1 ? "completed" : ""}`}
            >
              <div className="step-number">
                {step > 1 ? <MdCheckCircle /> : "1"}
              </div>
              <span>Brand Identity</span>
            </div>
            <div
              className={`step-item ${step === 2 ? "active" : step > 2 ? "completed" : ""}`}
            >
              <div className="step-number">
                {step > 2 ? <MdCheckCircle /> : "2"}
              </div>
              <span>Global Config</span>
            </div>
            <div className={`step-item ${step === 3 ? "active" : ""}`}>
              <div className="step-number">3</div>
              <span>Service Health</span>
            </div>
          </div>
        </div>

        <div className="installation-content">
          <div className="content-header">
            <h2>
              {step === 1
                ? "Brand Identity"
                : step === 2
                  ? "Global Configuration"
                  : "Service Health"}
            </h2>
            <p className="text-muted">
              {step === 1
                ? "Define your platform's visual identity."
                : step === 2
                  ? "Set up your primary domain and communication channels."
                  : "Verify your infrastructure status before finishing."}
            </p>
          </div>

          <div className="step-content">
            {step === 1 && (
              <div className="animate-fade-in">
                <div className="mb-4">
                  <label className="form-label">Application Name</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdBusiness />
                    </span>
                    <input
                      type="text"
                      name="appName"
                      className="form-control"
                      value={formData.appName}
                      onChange={handleChange}
                      placeholder="e.g. Microsite Cloud Inc."
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="form-label">App Logo Upload</label>
                  <div className="logo-upload-box">
                    <MdCloudUpload size={40} className="mb-2 text-primary" />
                    <p>Click to upload or drag logo here</p>
                    <small className="text-muted">
                      Supports PNG, SVG, JPG (Max 2MB)
                    </small>
                    <input type="file" className="d-none" />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="animate-fade-in">
                <div className="mb-4">
                  <label className="form-label">Primary Domain</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdSettings />
                    </span>
                    <input
                      type="text"
                      name="primaryDomain"
                      className="form-control"
                      value={formData.primaryDomain}
                      onChange={handleChange}
                      placeholder="portal.yourdomain.com"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="form-label">Admin Notification Email</label>
                  <div className="input-group">
                    <span className="input-group-text">@</span>
                    <input
                      type="email"
                      name="adminEmail"
                      className="form-control"
                      value={formData.adminEmail}
                      onChange={handleChange}
                      placeholder="admin@yourdomain.com"
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="animate-fade-in">
                <div className="health-grid">
                  <div className={`health-item ${health.database}`}>
                    <div className="health-icon">
                      <MdHealthAndSafety />
                    </div>
                    <div className="health-info">
                      <h6>Database (MongoDB)</h6>
                      <span className="status-text">{health.database}</span>
                    </div>
                  </div>
                  <div className={`health-item ${health.kafka}`}>
                    <div className="health-icon">
                      <MdHealthAndSafety />
                    </div>
                    <div className="health-info">
                      <h6>Message Broker (Kafka)</h6>
                      <span className="status-text">{health.kafka}</span>
                    </div>
                  </div>
                  <div className={`health-item ${health.redis}`}>
                    <div className="health-icon">
                      <MdHealthAndSafety />
                    </div>
                    <div className="health-info">
                      <h6>Cache Store (Redis)</h6>
                      <span className="status-text">{health.redis}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="content-footer d-flex justify-content-between">
            {step > 1 ? (
              <button
                className="btn btn-outline-secondary px-4"
                onClick={handlePrev}
              >
                Previous
              </button>
            ) : (
              <div></div>
            )}

            {step < 3 ? (
              <button className="btn btn-primary px-5" onClick={handleNext}>
                Continue to Step {step + 1}
              </button>
            ) : (
              <button
                className="btn btn-dark px-5"
                onClick={handleSubmit}
                disabled={
                  loading || Object.values(health).some((s) => s !== "online")
                }
              >
                {loading ? "Finishing Setup..." : "Complete Installation"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallationPage;
